import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import { useSiteContext } from '../../context/SiteContext';
import * as roomServices from '../../services/roomServices';
import RLinkLogo from '../common/RLinkLogo';
import { useWhiteLabel } from '../../context/WhiteLabelContext';
import httpServices from '../../services/httpServices';

export function LeaveScreenNew({ setIsMeetingLeft, meetingId }) {
  const [selectedRating, setSelectedRating] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [audioQuality, setAudioQuality] = useState("Excellent");
  const [videoQuality, setVideoQuality] = useState("Excellent");
  const [lastMeetingSession, setLastMeetingSession] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { meetingSettings, isAuthenticated } = useSiteContext();
  const { branding: wlBranding } = useWhiteLabel();

  const navigate = useNavigate();

  useEffect(() => {
    getLastMeetingSession();
  }, []);

  // Recorder-side guard: if Agora's headless recorder bot has landed on this
  // Session Complete screen, the meeting is over but the recorder is still
  // capturing. Fire /api/recording/stop so the backend tears down the Agora
  // session immediately instead of waiting for Agora's maxRecordingHour cap
  // (which otherwise gives us hours of Session Complete footage on S3).
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const isRecorder = params.get('recorder') === 'true';
      if (!isRecorder) return;
      const targetMeetingId = meetingId || meetingSettings?.roomId;
      if (!targetMeetingId) return;
      console.log('[RecorderGuard] Session Complete detected — stopping recording for', targetMeetingId);
      httpServices
        .post('/recording/stop', { meetingId: targetMeetingId })
        .catch((err) => {
          console.warn('[RecorderGuard] stopRecording failed (backend watchdog will catch this):', err?.response?.data || err?.message);
        });
    } catch (err) {
      console.warn('[RecorderGuard] guard setup failed:', err?.message);
    }
  }, [meetingId, meetingSettings?.roomId]);


  const getLastMeetingSession = async () => {
    const response = await roomServices.getLastMeetingSession(meetingSettings.roomId);
    setLastMeetingSession(response.data);
  };

  const handleRatingSelect = (rating) => {
    setSelectedRating(rating);
  };

  const handleSubmitFeedback = async () => {

    if (!selectedRating) {
      toast.error("Please select a rating before submitting feedback.", {
        position: "bottom-left",
        autoClose: 3000,
        hideProgressBar: true,
        closeButton: false,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
      });
      return;
    }

    // Handle feedback submission here
    console.log("Rating:", selectedRating);
    console.log("Feedback:", feedback);
    console.log("Audio Quality:", audioQuality);
    console.log("Video Quality:", videoQuality);


    const foundEmoji = emojis.find((emoji) => emoji.id === selectedRating);

    const feedbackData = {
      meetingExperience: foundEmoji?.label || null,
      feedback: feedback,
      audioQuality: audioQuality,
      videoQuality: videoQuality,
      roomId: meetingSettings.roomId,
      name: localStorage.getItem("participantName"),
    };

    setIsSubmitting(true);
    try {
      await roomServices.submitFeedback(feedbackData);
      toast.success("Thank you for your feedback!", {
        position: "bottom-left",
        autoClose: 2000,
        hideProgressBar: true,
        closeButton: false,
        theme: "light",
      });
      navigate("/");
    } catch (error) {
      console.error("Feedback submission failed:", error);
      toast.error("Failed to submit feedback. Please try again.", {
        position: "bottom-left",
        autoClose: 3000,
        hideProgressBar: true,
        closeButton: false,
        theme: "light",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRejoinRoom = () => {
    const rejoinId = localStorage.getItem("rejoinId");
    localStorage.removeItem("rejoinId");
    window.location.href = rejoinId;
    // setIsMeetingLeft(false);
  };

  // Ordered worst → best (left → right) to match the 5-star rating convention
  // where 1 = lowest, 5 = highest. Only the `label` is sent to the backend
  // (handleSubmitFeedback maps id → label), so renumbering ids here doesn't
  // change the persisted feedback payload.
  const emojis = [
    { id: 1, emoji: "/icons/verySad.png", label: "Very sad" },
    { id: 2, emoji: "/icons/sad.png", label: "Sad" },
    { id: 3, emoji: "/icons/neutral.png", label: "Neutral" },
    { id: 4, emoji: "/icons/happy.png", label: "Happy" },
    { id: 5, emoji: "/icons/veryHappy.png", label: "Very happy" },
  ];

  const qualityOptions = ["Excellent", "Good", "Fair", "Poor"];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#001233] to-[#6a1fbf] flex items-center justify-center py-8 md:py-16 px-4">
      <div className="max-w-4xl w-full mx-2 md:mx-4">
        {/* Header Section */}
        <div className="text-center mb-4 md:mb-6">
          {/* Logo */}
          <div className="mb-4 md:mb-6 flex justify-center">
            <RLinkLogo size="xl" companyLogo={wlBranding.companyLogo} companyName={wlBranding.companyName} />
          </div>

          {/* Session Complete */}
          <h1 className="font-['Raleway'] font-bold text-white text-3xl md:text-5xl mb-3 uppercase tracking-wider">
            Session Complete
          </h1>

          {/* Subtitle */}
          <p className="font-['Open_Sans'] text-white/90 text-base md:text-xl mb-6 md:mb-8">
            Your meeting has ended successfully.
          </p>

          {/* Thank you message */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-xl p-4 md:p-5 mb-6 md:mb-8 mx-2 md:mx-8">
            <p className="font-['Open_Sans'] text-[#00c853] text-sm md:text-lg font-medium">
              Thanks for using {wlBranding.companyName || 'R-Link'}, your space for meaningful, rewarding
              conversations.
            </p>
          </div>

          {/* Meeting Details */}
          <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-8 mb-6 md:mb-8 text-white/90 px-4">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-[#00c853]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-['Open_Sans'] text-sm md:text-base">Duration: {lastMeetingSession?.duration || '0:00'}</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-[#00c853]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span className="font-['Open_Sans'] text-sm md:text-base">{lastMeetingSession?.totalParticipants || 0} Participants</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-[#00c853]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
              </svg>
              <span className="font-['Open_Sans'] text-sm md:text-base">
                Connection: Excellent
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-4">
            {!wlBranding.isWhiteLabel && (
              <button
                onClick={() => {
                  window.open("https://r-link.com", "_blank");
                }}
                className="font-['Open_Sans'] font-bold bg-[#6a1fbf] hover:bg-[#5a19a0] text-white px-6 md:px-8 text-sm md:text-base py-3 rounded-lg transition-colors duration-200 w-full max-w-xs md:w-auto"
              >
                Start Earning With R-Link
              </button>
            )}
            {isAuthenticated && (
              <button
                onClick={() => navigate("/dashboard")}
                className="font-['Open_Sans'] font-bold bg-[#00c853] hover:bg-[#00a844] text-white px-6 md:px-8 text-sm md:text-base py-3 rounded-lg transition-colors duration-200 w-full max-w-xs md:w-auto"
              >
                Go to Dashboard
              </button>
            )}
          </div>
        </div>

        {/* Feedback Section - Dark Card per brand guide */}
        <div className="mb-6 md:mb-8 bg-[#2a3955] rounded-xl p-6 md:p-8 border border-[#556177] w-full md:w-4/5 mx-auto">
          <h2 className="font-['Raleway'] font-bold text-white text-2xl mb-6 text-center">
            How Was Your Call?
          </h2>

          {/* Emoji Ratings */}
          <div className="flex justify-center gap-3 md:gap-5 mb-6">
            {emojis.map((emoji) => (
              <button
                key={emoji.id}
                onClick={() => handleRatingSelect(emoji.id)}
                className={`p-2 md:p-3 rounded-full transition-all duration-300 ${selectedRating === emoji.id
                  ? "bg-[#00c853] scale-110 shadow-lg"
                  : "bg-[#001233] hover:bg-[#001233]/70 hover:scale-105"
                  }`}
                title={emoji.label}
              >
                <img
                  src={emoji.emoji}
                  alt={emoji.label}
                  className={`w-8 h-8 md:w-12 md:h-12 object-contain transition-all duration-300 ${selectedRating === emoji.id
                    ? "brightness-110"
                    : ""
                    }`}
                />
              </button>
            ))}
          </div>

          {/* Quality Dropdowns */}
          <div className="flex flex-col md:flex-row justify-center gap-4 md:gap-8 mb-6">
            <div className="w-full md:w-44">
              <label className="font-['Open_Sans'] font-bold block text-white text-sm mb-2 text-center">
                Audio Quality
              </label>
              <select
                value={audioQuality}
                onChange={(e) => setAudioQuality(e.target.value)}
                className="font-['Open_Sans'] w-full bg-[#001233] border border-[#556177] rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00c853] focus:ring-2 focus:ring-[#00c853] focus:ring-opacity-20 text-sm md:text-base transition-all"
              >
                {qualityOptions.map((option) => (
                  <option
                    key={option}
                    value={option}
                    className="bg-[#001233] text-white"
                  >
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-full md:w-44">
              <label className="font-['Open_Sans'] font-bold block text-white text-sm mb-2 text-center">
                Video Quality
              </label>
              <select
                value={videoQuality}
                onChange={(e) => setVideoQuality(e.target.value)}
                className="font-['Open_Sans'] w-full bg-[#001233] border border-[#556177] rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00c853] focus:ring-2 focus:ring-[#00c853] focus:ring-opacity-20 text-sm md:text-base transition-all"
              >
                {qualityOptions.map((option) => (
                  <option
                    key={option}
                    value={option}
                    className="bg-[#001233] text-white"
                  >
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Text Area */}
          <div className="mb-6">
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Share your thoughts with us (optional)"
              className="font-['Open_Sans'] w-full h-24 md:h-32 bg-white rounded-lg p-3 md:p-4 text-[#001233] placeholder-[#556177] resize-none focus:outline-none focus:border-[#00c853] focus:ring-2 focus:ring-[#00c853] focus:ring-opacity-20 border border-[#cfcfcf] text-sm md:text-base transition-all"
            />
          </div>

          {/* Submit Feedback Button */}
          <div className="flex w-full items-center justify-center">
            <button
              onClick={handleSubmitFeedback}
              disabled={isSubmitting}
              className="font-['Open_Sans'] font-bold bg-[#00c853] hover:bg-[#00a844] text-white px-8 md:px-10 text-base py-3 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="font-['Open_Sans'] text-white/60 text-xs md:text-sm">
            {wlBranding.isWhiteLabel ? `Powered by ${wlBranding.companyName}` : 'Powered by R-Link blockchain technology'}
          </p>
        </div>
      </div>
    </div>
  );
}
